import { useState, useCallback, useEffect } from "react";

import type { CustomEasing } from "./types";

const STORAGE_KEY = "excalidraw-custom-easings";

const persist = (easings: CustomEasing[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(easings));
  return easings;
};

export const useEasingEditor = () => {
  const [customEasings, setCustomEasings] = useState<CustomEasing[]>([]);

  // Load custom easings from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          // drop hand-edited/corrupt entries instead of rendering
          // undefined-valued options
          setCustomEasings(
            parsed.filter(
              (e): e is CustomEasing =>
                typeof e?.id === "string" &&
                typeof e?.name === "string" &&
                typeof e?.value === "string",
            ),
          );
        }
      } catch (e) {
        console.error("Failed to load custom easings", e);
      }
    }
  }, []);

  const saveCustomEasing = useCallback((easing: string, name: string) => {
    setCustomEasings((prev) => {
      // same curve saved again → just rename it; same name → overwrite its
      // curve. Keeps repeated "Save & Use" from growing the list forever.
      const existing = prev.find((e) => e.value === easing || e.name === name);
      if (existing) {
        return persist(
          prev.map((e) => (e === existing ? { ...e, name, value: easing } : e)),
        );
      }
      return persist([
        ...prev,
        {
          id: `custom-${Date.now()}-${prev.length}`,
          name,
          value: easing,
          created: Date.now(),
        },
      ]);
    });
  }, []);

  const deleteCustomEasing = useCallback((id: string) => {
    setCustomEasings((prev) => persist(prev.filter((e) => e.id !== id)));
  }, []);

  return { customEasings, saveCustomEasing, deleteCustomEasing };
};
